import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useStore } from 'src/integration/store/store';
import useHttpService from 'src/toolkit/hooks/useHttpService';
import useUrlJavaBuilder from 'src/toolkit/utils/useUrlJavaBuilder';
import useErrorHandling from 'src/toolkit/utils/useErrorHandling';

interface RequestOptions extends SimpleObject {
  retry?: number;
  enabled?: boolean;
}

interface ReactQueryProps {
  endpoint: string;
  queryKey: string;
  body?: SimpleObject;
  queryPathId?: SimpleObject;
  queryParams?: SimpleObject;
  opts: RequestOptions;
  method?: 'POST' | 'GET';
}

const useReactQueryJava = <I, O>({
  endpoint,
  queryKey,
  body,
  queryParams,
  queryPathId,
  method = 'GET',
  opts,
}: ReactQueryProps) => {
  const { resolveJavaUrl } = useUrlJavaBuilder();
  const { formatBodyData } = useHttpService();
  const { configStore } = useStore();
  const { jwtToken } = configStore;
  const { logoutOnError } = useErrorHandling();

  const fetchConfig = {
    method,
    headers: {
      ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
      'Content-Type': 'application/json',
    },
    body: formatBodyData(body),
    options: opts,
  };

  const queryOptions: UseQueryOptions<I, O> = {
    queryKey: [queryKey],
    queryFn: async () => {
      const response = await fetch(resolveJavaUrl(endpoint, queryParams, queryPathId), fetchConfig);

      if (response.ok) {
        return await response.json();
      }

      if (response?.status === 401) {
        logoutOnError();
      }

      const error = new Error('Something is wrong');
      (error as any).status = response.status;
      throw error;
    },
    ...opts,
  };

  const queryResult = useQuery<I, O>(queryOptions);

  return {
    ...queryResult,
  };
};

export default useReactQueryJava;
